/**
 * Staff Hours & Payroll Cost — inline widget for StaffSchedule.jsx.
 *
 * Renders:
 *   • 4 summary cards (Scheduled / Worked / Payable / Estimated Cost)
 *   • Payroll Summary button beside the existing Print / Export controls
 *   • Full drawer with staff-level breakdown table, filters, approve/export/print
 *
 * Data contract (server owns permission masking):
 *   GET  /api/payroll/summary?start_date=&site_id=
 *   POST /api/payroll/periods/compute
 *   GET  /api/payroll/periods/:id
 *   PATCH /api/payroll/lines/:id/confirm
 *   POST /api/payroll/periods/:id/submit  | /approve | /reopen | /export.csv
 *
 * Care Workers get cost-stripped views from the server automatically.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BadgePoundSterling, Clock, ClipboardCheck, Timer, AlertTriangle, CheckCircle2,
  RefreshCcw, Undo2, Download, Printer, FileText, FileDown, Users,
} from "lucide-react";

const STATUS_STYLES = {
  draft:              "bg-slate-100 text-slate-700 border-slate-300",
  awaiting_approval:  "bg-amber-50 text-amber-800 border-amber-300",
  approved:           "bg-emerald-50 text-emerald-800 border-emerald-300",
  exported:           "bg-sky-50 text-sky-800 border-sky-300",
};
const STATUS_LABEL = {
  draft: "Draft",
  awaiting_approval: "Awaiting approval",
  approved: "Approved",
  exported: "Exported",
};

const money = (v, ccy = "GBP") =>
  v == null ? "—" : new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy }).format(v);
const num = (v) => (v == null ? "—" : Number(v).toFixed(2));

function SummaryCard({ icon: Icon, label, value, sub, tone = "info", testid }) {
  const tones = {
    info: "border-sky-200 bg-sky-50 text-sky-900",
    ok:   "border-emerald-200 bg-emerald-50 text-emerald-900",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    cost: "border-indigo-200 bg-indigo-50 text-indigo-900",
  };
  return (
    <div className={`border rounded-md p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-75 font-semibold flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-heading font-semibold mt-1 tabular-nums">{value}</div>
      <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>
    </div>
  );
}

export default function PayrollWidget({ weekStart, weekEnd, filterSite, user, canManage }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [period, setPeriod] = useState(null);
  const [lines, setLines] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [staffQuery, setStaffQuery] = useState("");
  const [confirmLine, setConfirmLine] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ actual_start: "", actual_end: "", reason: "" });
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [confirmWeekOpen, setConfirmWeekOpen] = useState(false);
  const [confirmPreview, setConfirmPreview] = useState(null);
  const [confirmPreviewLoading, setConfirmPreviewLoading] = useState(false);
  // Payroll Statement (Gross Pay Statement) — approved/exported periods only.
  const [statementIndex, setStatementIndex] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementBusy, setStatementBusy] = useState("");
  // Distribution log — period-scoped audit of statement downloads.
  const [distLogOpen, setDistLogOpen] = useState(false);
  const [distLog, setDistLog] = useState(null);
  const [distLogLoading, setDistLogLoading] = useState(false);

  const site = filterSite && filterSite !== "all" ? filterSite : undefined;
  const currency = period?.approved_currency || summary?.currency || "GBP";

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ start_date: weekStart });
      if (site) q.set("site_id", site);
      const r = await api.get(`/payroll/summary?${q.toString()}`);
      setSummary(r.data);
    } catch (e) {
      // silent — payroll may be unconfigured for this org yet
      setSummary(null);
    } finally { setLoading(false); }
  }, [weekStart, site]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const openDrawer = async () => {
    setDrawerOpen(true);
    setDrawerLoading(true);
    try {
      // (B) Only POST /periods/compute when we don't already have a period
      // for this week — otherwise just GET it. This prevents the drawer's
      // open action from triggering an unnecessary rebuild every time.
      // With backend (A) in place, even when compute *does* run it now
      // preserves manager-confirmed attendance, so this is defence in depth.
      if (canManage) {
        let periodId = summary?.period?.id;
        if (!periodId) {
          const c = await api.post("/payroll/periods/compute", { start_date: weekStart, site_id: site });
          periodId = c.data.id;
        }
        const r = await api.get(`/payroll/periods/${periodId}`);
        setPeriod(r.data.period);
        setLines(r.data.lines || []);
      } else if (summary?.period?.id) {
        const r = await api.get(`/payroll/periods/${summary.period.id}`);
        setPeriod(r.data.period);
        setLines(r.data.lines || []);
      } else {
        setPeriod(null);
        setLines([]);
      }
      // (C) Keep top summary cards + footer totals in sync with the drawer
      // rows so all three views ever represent the same persisted state.
      await loadSummary();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDrawerLoading(false); }
  };

  const refresh = async () => {
    if (!period?.id) return;
    setDrawerLoading(true);
    try {
      const r = await api.get(`/payroll/periods/${period.id}`);
      setPeriod(r.data.period);
      setLines(r.data.lines || []);
      await loadSummary();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDrawerLoading(false); }
  };

  const openConfirm = (ln) => {
    setConfirmLine(ln);
    setConfirmForm({
      actual_start: ln.actual_start || ln.scheduled_start || "",
      actual_end:   ln.actual_end   || ln.scheduled_end   || "",
      reason: "",
    });
  };
  const saveConfirm = async () => {
    if (!confirmLine) return;
    if (!confirmForm.reason || confirmForm.reason.length < 3) {
      toast.error("Please add a short reason so it's captured in the audit trail.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/payroll/lines/${confirmLine.id}/confirm`, confirmForm);
      toast.success(confirmLine.needs_confirmation ? "Line confirmed" : "Line updated");
      setConfirmLine(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doSubmit = async () => {
    setBusy(true);
    try {
      await api.post(`/payroll/periods/${period.id}/submit`);
      toast.success("Submitted for approval");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const doApprove = async () => {
    setBusy(true);
    try {
      await api.post(`/payroll/periods/${period.id}/approve`);
      toast.success("Approved — rates snapshotted");
      setConfirmWeekOpen(false);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const openConfirmWeek = async () => {
    if (!period?.id) return;
    setConfirmWeekOpen(true);
    setConfirmPreview(null);
    setConfirmPreviewLoading(true);
    try {
      const r = await api.get(`/payroll/periods/${period.id}/confirm-preview`);
      setConfirmPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirmWeekOpen(false);
    } finally { setConfirmPreviewLoading(false); }
  };

  const jumpToIncompleteStaff = (staffName) => {
    // Close the Confirm Week dialog and filter the underlying drawer so the
    // manager can resolve incomplete lines individually.
    setConfirmWeekOpen(false);
    if (staffName) setStaffQuery(staffName);
  };
  const doReopen = async () => {
    if (!reopenReason || reopenReason.length < 5) {
      toast.error("Reason must be at least 5 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/payroll/periods/${period.id}/reopen`, { reason: reopenReason });
      toast.success("Reopened");
      setReopenOpen(false); setReopenReason("");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const doExport = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/payroll/periods/${period.id}/export.csv`, null, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `payroll-${period.start_date}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const doExportXlsx = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/payroll/periods/${period.id}/export.xlsx`, null, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url; a.download = `payroll-${period.start_date}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("XLSX exported");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const q = new URLSearchParams();
      if (site) q.set("site_id", site);
      const [approved, exported] = await Promise.all([
        api.get(`/payroll/periods?status=approved&${q.toString()}`),
        api.get(`/payroll/periods?status=exported&${q.toString()}`),
      ]);
      const rows = [...(approved.data || []), ...(exported.data || [])]
        .sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""));
      setHistory(rows);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setHistoryLoading(false); }
  }, [site]);

  const openHistory = async () => {
    setHistoryOpen(true);
    await loadHistory();
  };

  const redownload = async (periodId, startDate, fmt) => {
    try {
      const r = await api.get(`/payroll/periods/${periodId}/download.${fmt}`, { responseType: "blob" });
      const mime = fmt === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url; a.download = `payroll-${startDate}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Payroll Statement (Gross Pay Statement) ----------------------
  const loadStatementIndex = useCallback(async () => {
    if (!period?.id || (period.status !== "approved" && period.status !== "exported")) {
      setStatementIndex(null);
      return;
    }
    setStatementLoading(true);
    try {
      const r = await api.get(`/payroll/periods/${period.id}/statement-index`);
      setStatementIndex(r.data);
    } catch (e) {
      // Non-fatal — panel just doesn't render
      setStatementIndex(null);
    } finally { setStatementLoading(false); }
  }, [period?.id, period?.status]);

  useEffect(() => { loadStatementIndex(); }, [loadStatementIndex]);

  const downloadStatement = async (staffUserId, staffName) => {
    if (!period?.id) return;
    setStatementBusy(staffUserId);
    try {
      const r = await api.get(`/payroll/periods/${period.id}/statement.pdf`, {
        params: { staff_user_id: staffUserId },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const safe = (staffName || "staff").replace(/[^A-Za-z0-9_\- ]/g, "_");
      a.href = url;
      a.download = `payroll-statement-${period.start_date}-${safe}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      await loadDistLog();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStatementBusy(""); }
  };

  const downloadAllStatements = async () => {
    if (!period?.id) return;
    setStatementBusy("__all__");
    try {
      const r = await api.get(`/payroll/periods/${period.id}/statements.zip`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll-statements-${period.start_date}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      await loadDistLog();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStatementBusy(""); }
  };

  // Distribution Log — tenant-scoped audit trail of statement downloads.
  const loadDistLog = useCallback(async () => {
    if (!period?.id || (period.status !== "approved" && period.status !== "exported")) {
      setDistLog(null);
      return;
    }
    setDistLogLoading(true);
    try {
      const r = await api.get(`/payroll/periods/${period.id}/distribution-log`);
      setDistLog(r.data);
    } catch (e) {
      setDistLog(null);
    } finally { setDistLogLoading(false); }
  }, [period?.id, period?.status]);

  useEffect(() => {
    if (distLogOpen) loadDistLog();
  }, [distLogOpen, loadDistLog]);

  const rows = useMemo(() => {
    const q = staffQuery.trim().toLowerCase();
    if (!q) return lines;
    return lines.filter((ln) => (ln.staff_name || "").toLowerCase().includes(q));
  }, [lines, staffQuery]);

  const pendingCount = lines.filter((ln) => ln.needs_confirmation).length;
  const status = period?.status || "draft";

  return (
    <>
      {/* Four summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="payroll-cards">
        <SummaryCard
          icon={Clock}
          label="Scheduled Hours"
          value={num(summary?.scheduled_hours)}
          sub={loading ? "Loading…" : `Week of ${weekStart}`}
          tone="info"
          testid="payroll-card-scheduled"
        />
        <SummaryCard
          icon={Timer}
          label="Worked Hours"
          value={num(summary?.worked_hours)}
          sub={pendingCount > 0 ? `${pendingCount} line${pendingCount === 1 ? "" : "s"} need confirm` : "Clock-in based"}
          tone="warn"
          testid="payroll-card-worked"
        />
        <SummaryCard
          icon={ClipboardCheck}
          label="Payable Hours"
          value={num(summary?.payable_hours)}
          sub={summary?.overtime_hours ? `Includes ${num(summary?.overtime_hours)}h overtime` : "Manager-approved"}
          tone="ok"
          testid="payroll-card-payable"
        />
        {summary?.can_see_cost !== false ? (
          <SummaryCard
            icon={BadgePoundSterling}
            label={
              summary?.projected?.cost_source === "snapshot"
                ? "Approved labour cost"
                : "Projected labour cost"
            }
            value={money(summary?.estimated_cost, currency)}
            sub={
              summary?.projected?.cost_source === "snapshot"
                ? "Rate snapshot locked"
                : "Rates snapshot on approval"
            }
            tone="cost"
            testid="payroll-card-cost"
          />
        ) : (
          <div className="border rounded-md p-4 border-slate-200 bg-slate-50 text-slate-500 flex items-center justify-center" data-testid="payroll-card-cost-hidden">
            <div className="text-[11px] uppercase tracking-widest">Cost hidden</div>
          </div>
        )}
      </div>

      {/* Payroll Summary button — mount inline so caller can place it wherever */}
      <div className="flex items-center justify-end -mt-2 gap-2">
        {canManage && (
          <Button
            variant="ghost" size="sm" onClick={openHistory} data-testid="btn-payroll-history"
            className="h-9"
          >
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Previous periods
          </Button>
        )}
        <Button
          variant="outline" size="sm" onClick={openDrawer} data-testid="btn-payroll-summary"
          className="h-9"
        >
          <FileText className="w-3.5 h-3.5 mr-1.5" /> Payroll Summary
        </Button>
      </div>

      {/* Drawer / dialog */}
      <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DialogContent className="w-[95vw] max-w-[1400px] sm:max-w-[1400px] max-h-[85vh] overflow-hidden flex flex-col" data-testid="payroll-drawer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              Payroll Summary — Week of {weekStart}
              {period?.status && (
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_STYLES[period.status]}`}
                      data-testid="payroll-status-pill">
                  {STATUS_LABEL[period.status]}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {canManage
                ? "Confirm attendance gaps, then approve to snapshot pay rates for this period. Historical payroll never changes when a rate is later updated."
                : "Your own hours for this week. Rates and costs are not visible."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 py-2 border-b border-slate-200 print:hidden">
            <Input
              placeholder="Filter by staff…"
              value={staffQuery}
              onChange={(e) => setStaffQuery(e.target.value)}
              className="h-9 w-56 text-sm"
              data-testid="payroll-filter-staff"
            />
            <div className="flex-1" />
            <Button variant="ghost" size="sm" onClick={refresh} disabled={drawerLoading} className="h-9" data-testid="btn-payroll-refresh">
              <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
            {canManage && status === "draft" && (
              <Button size="sm" onClick={doSubmit} disabled={busy || pendingCount > 0} className="h-9" data-testid="btn-payroll-submit">
                Submit for Approval
              </Button>
            )}
            {canManage && (status === "draft" || status === "awaiting_approval") && (
              <Button size="sm" onClick={openConfirmWeek} disabled={busy}
                      className="h-9 bg-emerald-700 hover:bg-emerald-800" data-testid="btn-payroll-confirm-week">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Confirm Week
              </Button>
            )}
            {canManage && (status === "approved" || status === "exported") && (
              <Button variant="ghost" size="sm" onClick={() => setDistLogOpen((v) => !v)}
                      className="h-9" data-testid="btn-payroll-distribution-log">
                <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />
                {distLogOpen ? "Hide Log" : "Distribution Log"}
              </Button>
            )}
            {canManage && (status === "approved" || status === "exported") && user?.role === "admin" && (
              <Button variant="outline" size="sm" onClick={() => setReopenOpen(true)} disabled={busy} className="h-9" data-testid="btn-payroll-reopen">
                <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Reopen
              </Button>
            )}
            {canManage && (status === "approved" || status === "exported") && user?.role === "admin" && (
              <Button size="sm" onClick={doExport} disabled={busy}
                      className="h-9 bg-sky-700 hover:bg-sky-800" data-testid="btn-payroll-export">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
              </Button>
            )}
            {canManage && (status === "approved" || status === "exported") && user?.role === "admin" && (
              <Button size="sm" onClick={doExportXlsx} disabled={busy}
                      variant="outline" className="h-9" data-testid="btn-payroll-export-xlsx">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Export XLSX
              </Button>
            )}
            {canManage && (status === "approved" || status === "exported") && (
              <Button size="sm" onClick={downloadAllStatements}
                      disabled={statementBusy === "__all__" || !statementIndex?.staff?.length}
                      variant="outline" className="h-9"
                      data-testid="btn-payroll-statements-all">
                <FileDown className="w-3.5 h-3.5 mr-1.5" />
                {statementBusy === "__all__" ? "Bundling…" : "Download All Statements"}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => window.print()} className="h-9" data-testid="btn-payroll-print">
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Print
            </Button>
          </div>

          {/* Payroll Statement panel — approved/exported periods only */}
          {canManage && (status === "approved" || status === "exported") && (
            <div className="my-2 rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2 print:hidden"
                 data-testid="payroll-statements-panel">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-indigo-700" />
                <div className="text-xs font-semibold text-indigo-900 uppercase tracking-wider">
                  Payroll Statements (Gross Pay)
                </div>
                <div className="flex-1" />
                <div className="text-[11px] text-indigo-700/80">
                  For internal payroll records only — not a statutory payslip
                </div>
              </div>
              {statementLoading ? (
                <div className="text-xs text-indigo-800/80 py-1">Loading staff list…</div>
              ) : !statementIndex?.staff?.length ? (
                <div className="text-xs text-indigo-800/80 py-1">
                  No approved staff timesheet lines to build a statement from.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {statementIndex.staff.map((s) => (
                    <Button
                      key={s.staff_user_id}
                      size="sm" variant="outline"
                      className="h-7 text-xs border-indigo-300 bg-white hover:bg-indigo-100 text-indigo-900"
                      disabled={statementBusy === s.staff_user_id}
                      onClick={() => downloadStatement(s.staff_user_id, s.staff_name)}
                      data-testid={`btn-payroll-statement-${s.staff_user_id}`}
                    >
                      <FileDown className="w-3 h-3 mr-1" />
                      {statementBusy === s.staff_user_id ? "Building…" : s.staff_name}
                      <span className="ml-1 text-indigo-500 tabular-nums">
                        · {Number(s.payable_hours || 0).toFixed(2)}h
                      </span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Distribution Log — audit trail of every statement download for this period */}
          {canManage && (status === "approved" || status === "exported") && distLogOpen && (
            <div className="my-2 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 print:hidden"
                 data-testid="payroll-distribution-log">
              <div className="flex items-center gap-2 mb-1">
                <ClipboardCheck className="w-4 h-4 text-slate-700" />
                <div className="text-xs font-semibold text-slate-800 uppercase tracking-wider">
                  Distribution Log
                </div>
                <div className="flex-1" />
                <div className="text-[11px] text-slate-500">
                  Append-only · CQC traceability
                </div>
              </div>
              {distLogLoading ? (
                <div className="text-xs text-slate-600 py-1">Loading log…</div>
              ) : !distLog?.events?.length ? (
                <div className="text-xs text-slate-600 py-1">
                  No statement downloads recorded for this period yet.
                </div>
              ) : (
                <div className="overflow-auto max-h-56">
                  <table className="w-full text-xs" data-testid="payroll-distribution-log-table">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-200">
                        <th className="text-left font-semibold py-1 pr-2">When</th>
                        <th className="text-left font-semibold py-1 pr-2">By</th>
                        <th className="text-left font-semibold py-1 pr-2">Staff</th>
                        <th className="text-left font-semibold py-1 pr-2">Fmt</th>
                        <th className="text-right font-semibold py-1 pr-2">Hours</th>
                        <th className="text-right font-semibold py-1 pr-2">Cost</th>
                        <th className="text-left font-semibold py-1">Outcome</th>
                      </tr>
                    </thead>
                    <tbody>
                      {distLog.events.map((r) => (
                        <tr key={r.id} className="border-b border-slate-100"
                            data-testid={`payroll-distribution-log-row-${r.id}`}>
                          <td className="py-1 pr-2 tabular-nums text-slate-700">
                            {(r.downloaded_at || "").slice(0, 19).replace("T", " ")}
                          </td>
                          <td className="py-1 pr-2 text-slate-700">
                            {r.downloaded_by_name || r.downloaded_by_email || "—"}
                          </td>
                          <td className="py-1 pr-2 text-slate-700">
                            {r.staff_user_id === "__all__" ? (
                              <span className="text-indigo-700 font-medium">All staff</span>
                            ) : (r.staff_name || r.staff_user_id?.slice(0, 8) || "—")}
                          </td>
                          <td className="py-1 pr-2 uppercase text-[10px] text-slate-500">
                            {r.format || "—"}
                          </td>
                          <td className="py-1 pr-2 text-right tabular-nums">
                            {r.hours_covered != null ? Number(r.hours_covered).toFixed(2) + "h" : "—"}
                          </td>
                          <td className="py-1 pr-2 text-right tabular-nums">
                            {r.estimated_cost_at_download != null
                              ? money(r.estimated_cost_at_download, confirmPreview?.currency || "GBP")
                              : "—"}
                          </td>
                          <td className="py-1">
                            {r.outcome === "success" ? (
                              <span className="pill bg-emerald-50 text-emerald-700 border-emerald-200">Success</span>
                            ) : (
                              <span className="pill bg-rose-50 text-rose-700 border-rose-200"
                                    title={r.reason || ""}>
                                Denied{r.reason ? ` · ${r.reason}` : ""}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {pendingCount > 0 && canManage && (
            <div className="my-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 print:hidden">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                {pendingCount} shift line{pendingCount === 1 ? "" : "s"} need attendance confirmation before you can submit or approve.
              </div>
            </div>
          )}

          <div className="flex-1 overflow-auto -mx-6 px-6">
            {drawerLoading ? (
              <div className="text-sm text-slate-500 py-8 text-center" data-testid="payroll-drawer-loading">Loading payroll data…</div>
            ) : rows.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center" data-testid="payroll-drawer-empty">
                No timesheet lines for this week yet.
              </div>
            ) : (
              <table className="data-table" data-testid="payroll-lines-table">
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Date</th>
                    <th>Shift</th>
                    <th className="text-right">Sched</th>
                    <th className="text-right">Worked</th>
                    <th className="text-right">Payable</th>
                    <th className="text-right">OT</th>
                    <th>Flags</th>
                    {(summary?.can_see_cost !== false) && <th className="text-right">Rate</th>}
                    {(summary?.can_see_cost !== false) && <th className="text-right">Cost</th>}
                    <th className="text-right print:hidden">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((ln) => (
                    <tr key={ln.id} data-testid={`payroll-line-${ln.id}`}
                        className={ln.needs_confirmation ? "bg-amber-50/50" : ""}>
                      <td className="font-medium">{ln.staff_name || ln.staff_user_id?.slice(0, 8)}</td>
                      <td className="text-xs">{(ln.scheduled_start || "").slice(0, 10)}</td>
                      <td className="text-xs mono">
                        {(ln.scheduled_start || "").slice(11, 16)}–{(ln.scheduled_end || "").slice(11, 16)}
                      </td>
                      <td className="text-right tabular-nums">{num(ln.scheduled_hours)}</td>
                      <td className="text-right tabular-nums">{num(ln.worked_hours)}</td>
                      <td className="text-right tabular-nums font-medium">{num(ln.payable_hours)}</td>
                      <td className="text-right tabular-nums">{num(ln.overtime_hours)}</td>
                      <td className="text-xs">
                        {ln.is_weekend && <span className="pill bg-sky-50 text-sky-700 border-sky-200 mr-1">Weekend</span>}
                        {ln.is_bank_holiday && <span className="pill bg-violet-50 text-violet-700 border-violet-200 mr-1">Bank hol</span>}
                        {ln.is_sleep_in && <span className="pill bg-slate-800 text-white mr-1">Sleep-in</span>}
                        {ln.needs_confirmation && <span className="pill bg-amber-50 text-amber-800 border-amber-300">Needs confirm</span>}
                        {ln.confirmed_by && <span className="pill bg-emerald-50 text-emerald-700 border-emerald-200">Confirmed</span>}
                      </td>
                      {(summary?.can_see_cost !== false) && (
                        <td className="text-right tabular-nums text-xs">
                          {ln.rate_snapshot?.base_rate != null ? money(ln.rate_snapshot.base_rate, currency) + "/h" : "—"}
                        </td>
                      )}
                      {(summary?.can_see_cost !== false) && (
                        <td className="text-right tabular-nums font-medium">
                          {money(ln.estimated_cost, currency)}
                        </td>
                      )}
                      <td className="text-right print:hidden">
                        {canManage && (status === "draft" || status === "awaiting_approval") && (
                          ln.needs_confirmation ? (
                            <Button size="sm" variant="outline" onClick={() => openConfirm(ln)} className="h-7 text-xs" data-testid={`btn-confirm-line-${ln.id}`}>
                              Confirm
                            </Button>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => openConfirm(ln)} className="h-7 text-xs" data-testid={`btn-edit-line-${ln.id}`}>
                              Edit
                            </Button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-semibold bg-slate-50">
                    <td colSpan={3} className="text-right">Totals</td>
                    <td className="text-right tabular-nums">{num(summary?.scheduled_hours)}</td>
                    <td className="text-right tabular-nums">{num(summary?.worked_hours)}</td>
                    <td className="text-right tabular-nums">{num(summary?.payable_hours)}</td>
                    <td className="text-right tabular-nums">{num(summary?.overtime_hours)}</td>
                    <td />
                    {(summary?.can_see_cost !== false) && <td />}
                    {(summary?.can_see_cost !== false) && (
                      <td className="text-right tabular-nums">{money(summary?.estimated_cost, currency)}</td>
                    )}
                    <td className="print:hidden" />
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Week dialog — final gate before snapshotting rates */}
      <Dialog open={confirmWeekOpen} onOpenChange={setConfirmWeekOpen}>
        <DialogContent className="max-w-2xl" data-testid="confirm-week-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              Confirm Week — {confirmPreview?.period?.start_date} → {confirmPreview?.period?.end_date}
            </DialogTitle>
            <DialogDescription>
              Confirming locks pay rates for this week. Historical figures never change if a rate is later updated.
            </DialogDescription>
          </DialogHeader>

          {confirmPreviewLoading ? (
            <div className="py-6 text-sm text-slate-500 text-center" data-testid="confirm-week-loading">
              Loading week summary…
            </div>
          ) : !confirmPreview ? null : (
            <div className="space-y-4">
              {/* Empty-period banner — never allow snapshotting an empty period */}
              {confirmPreview.is_empty ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                     data-testid="confirm-week-empty">
                  No payroll records found for this period. There are no worked or payable hours available to confirm.
                </div>
              ) : null}
              {/* Totals grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="confirm-week-totals">
                <div className="border rounded-md p-3 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Staff</div>
                  <div className="text-xl font-heading tabular-nums" data-testid="confirm-week-staff-count">
                    {confirmPreview.staff_count}
                  </div>
                </div>
                <div className="border rounded-md p-3 bg-sky-50">
                  <div className="text-[10px] uppercase tracking-widest text-sky-700 font-semibold">Scheduled</div>
                  <div className="text-xl font-heading tabular-nums">{num(confirmPreview.totals.scheduled_hours)}h</div>
                </div>
                <div className="border rounded-md p-3 bg-amber-50">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Worked</div>
                  <div className="text-xl font-heading tabular-nums">{num(confirmPreview.totals.worked_hours)}h</div>
                </div>
                <div className="border rounded-md p-3 bg-emerald-50">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Payable</div>
                  <div className="text-xl font-heading tabular-nums" data-testid="confirm-week-payable">
                    {num(confirmPreview.totals.payable_hours)}h
                  </div>
                </div>
                <div className="border rounded-md p-3 bg-orange-50">
                  <div className="text-[10px] uppercase tracking-widest text-orange-700 font-semibold">Overtime</div>
                  <div className="text-xl font-heading tabular-nums" data-testid="confirm-week-overtime">
                    {num(confirmPreview.preview?.overtime_hours ?? confirmPreview.totals.overtime_hours)}h
                  </div>
                </div>
                <div className="border rounded-md p-3 bg-indigo-50">
                  <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-semibold">Est. Cost</div>
                  <div className="text-xl font-heading tabular-nums" data-testid="confirm-week-cost">
                    {money(
                      confirmPreview.preview?.total_estimated_cost
                        ?? confirmPreview.totals.estimated_cost,
                      confirmPreview.currency,
                    )}
                  </div>
                  <div className="text-[10px] text-indigo-700/80 mt-1"
                       data-testid="confirm-week-cost-note">
                    {confirmPreview.preview?.cost_source === "snapshot"
                      ? "Snapshot — locked at approval"
                      : "Projected — locked when approved"}
                  </div>
                </div>
              </div>

              {/* Missing-pay-config banner — informational only, does NOT
                  block approval (matches existing manager-judgement rule). */}
              {(confirmPreview.preview?.missing_config_staff_count || 0) > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                     data-testid="confirm-week-missing-config">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
                    <div className="flex-1">
                      <div className="font-semibold mb-1">
                        {confirmPreview.preview.missing_config_staff_count} staff
                        {" "}member{confirmPreview.preview.missing_config_staff_count === 1 ? "" : "s"}
                        {" "}have no active pay configuration
                      </div>
                      <div className="text-xs mb-2">
                        Their hours will approve at <span className="font-mono">£0</span> unless
                        you add a pay configuration first. This does not block confirmation.
                      </div>
                      <ul className="space-y-1 max-h-40 overflow-auto">
                        {(confirmPreview.preview.by_staff || [])
                          .filter((s) => s.has_config === false)
                          .map((s) => (
                            <li key={s.staff_user_id}
                                className="text-xs bg-white/60 rounded px-2 py-1 flex items-center justify-between"
                                data-testid={`confirm-week-missing-${s.staff_user_id}`}>
                              <span className="font-medium">{s.staff_name}</span>
                              <span className="text-amber-800 tabular-nums">
                                {num(s.hours)}h · will approve at £0
                              </span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Incomplete-lines-by-staff block */}
              {confirmPreview.incomplete.line_count > 0 ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="confirm-week-incomplete">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
                    <div className="flex-1 text-sm text-amber-900">
                      <div className="font-semibold mb-1">
                        {confirmPreview.incomplete.line_count} incomplete timesheet line{confirmPreview.incomplete.line_count === 1 ? "" : "s"}
                        {" "}across {confirmPreview.incomplete.staff_count} staff member{confirmPreview.incomplete.staff_count === 1 ? "" : "s"}
                      </div>
                      <div className="text-xs mb-2">
                        Resolve each staff member's clock-in/out individually before confirming the week.
                      </div>
                      <ul className="space-y-1 max-h-40 overflow-auto">
                        {confirmPreview.incomplete.staff.map((s) => (
                          <li key={s.staff_user_id} className="flex items-center justify-between text-xs bg-white/60 rounded px-2 py-1"
                              data-testid={`confirm-week-incomplete-${s.staff_user_id}`}>
                            <span className="font-medium">{s.staff_name}</span>
                            <span className="flex items-center gap-2">
                              <span className="text-amber-800">
                                {s.line_count} shift{s.line_count === 1 ? "" : "s"}
                              </span>
                              <Button
                                size="sm" variant="outline"
                                className="h-6 text-[11px] px-2"
                                onClick={() => jumpToIncompleteStaff(s.staff_name)}
                                data-testid={`btn-jump-resolve-${s.staff_user_id}`}
                              >
                                Resolve
                              </Button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900"
                     data-testid="confirm-week-clean">
                  All timesheet lines are complete. You're ready to confirm.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmWeekOpen(false)} data-testid="btn-confirm-week-cancel">
              Cancel
            </Button>
            <Button
              onClick={doApprove}
              disabled={busy || !confirmPreview?.can_confirm}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="btn-confirm-week-final"
            >
              {busy ? "Confirming…"
                : confirmPreview?.is_empty
                  ? "No records to confirm"
                : confirmPreview?.can_confirm
                  ? `Confirm & Snapshot Rates`
                  : `Resolve ${confirmPreview?.incomplete?.line_count || ""} line(s) first`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm actual hours dialog — also reused to Edit an already-
          confirmed line while the period is still draft / awaiting_approval.
          The backend endpoint (PATCH /api/payroll/lines/{id}/confirm) already
          supports both flows: it recomputes worked_hours, refuses on
          approved/exported periods, records an audit event with reason, and
          recomputes period totals. No API change is introduced here. */}
      <Dialog open={!!confirmLine} onOpenChange={(o) => !o && setConfirmLine(null)}>
        <DialogContent data-testid="confirm-line-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirmLine && !confirmLine.needs_confirmation ? "Edit confirmed hours" : "Confirm actual hours"}
            </DialogTitle>
            <DialogDescription>
              {confirmLine && !confirmLine.needs_confirmation
                ? "Update the actual start/end for this already-confirmed line."
                : "Clock-in data is incomplete. Enter the true start/end. This creates an audit event."}
            </DialogDescription>
          </DialogHeader>
          {confirmLine && !confirmLine.needs_confirmation && (
            <div data-testid="edit-confirmed-note"
                 className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800">
              Editing a confirmed line. Previous values will be replaced. A reason is required and the change will be recorded in the audit trail.
            </div>
          )}
          {confirmLine && !confirmLine.needs_confirmation && (
            <div data-testid="edit-confirmed-existing"
                 className="text-xs text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 space-y-0.5">
              <div>Current payable hours: <span className="tabular-nums font-medium">{num(confirmLine.payable_hours)}h</span></div>
              {confirmLine.confirmed_by && (
                <div>Previously confirmed by: <span className="font-medium">{confirmLine.confirmed_by}</span></div>
              )}
              {confirmLine.confirmed_at && (
                <div>Previously confirmed at: <span className="tabular-nums">{String(confirmLine.confirmed_at).slice(0, 16).replace("T", " ")}</span></div>
              )}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-medium">Actual start</Label>
              <Input type="datetime-local" value={(confirmForm.actual_start || "").slice(0, 16)}
                     onChange={(e) => setConfirmForm({ ...confirmForm, actual_start: e.target.value + ":00" })}
                     className="mt-1 h-9" data-testid="confirm-actual-start" />
            </div>
            <div>
              <Label className="text-xs font-medium">Actual end</Label>
              <Input type="datetime-local" value={(confirmForm.actual_end || "").slice(0, 16)}
                     onChange={(e) => setConfirmForm({ ...confirmForm, actual_end: e.target.value + ":00" })}
                     className="mt-1 h-9" data-testid="confirm-actual-end" />
            </div>
            <div>
              <Label className="text-xs font-medium">Reason (audit)</Label>
              <Textarea rows={2} value={confirmForm.reason}
                        onChange={(e) => setConfirmForm({ ...confirmForm, reason: e.target.value })}
                        placeholder="e.g. Staff forgot to clock out; confirmed on phone"
                        className="mt-1 text-sm" data-testid="confirm-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmLine(null)}>Cancel</Button>
            <Button onClick={saveConfirm} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-confirm-line-save">
              {busy ? "Saving…" : "Save & audit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen dialog */}
      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent data-testid="reopen-dialog">
          <DialogHeader>
            <DialogTitle>Reopen payroll period</DialogTitle>
            <DialogDescription>Provide a reason — it will be stored on the audit event.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={reopenReason} onChange={(e) => setReopenReason(e.target.value)}
                    placeholder="Reason for reopening (min 5 chars)"
                    className="text-sm" data-testid="reopen-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)}>Cancel</Button>
            <Button onClick={doReopen} disabled={busy} data-testid="btn-reopen-confirm">Reopen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll History dialog — approved + exported periods with re-download */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="payroll-history-dialog">
          <DialogHeader>
            <DialogTitle>Payroll history — previous periods</DialogTitle>
            <DialogDescription>
              Every payroll period that has been approved or exported. Re-download the CSV or
              XLSX at any time — the file is regenerated from the immutable rate snapshots so
              historical numbers cannot drift.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto -mx-6 px-6">
            {historyLoading ? (
              <div className="text-sm text-slate-500 py-8 text-center" data-testid="payroll-history-loading">
                Loading previous periods…
              </div>
            ) : history.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center" data-testid="payroll-history-empty">
                No previous payroll periods yet. Approve a week to see it here.
              </div>
            ) : (
              <table className="data-table" data-testid="payroll-history-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Status</th>
                    <th>Approved by</th>
                    <th>Exported by</th>
                    <th>Lines</th>
                    <th className="text-right">Total cost</th>
                    <th className="text-right">Re-download</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((p) => (
                    <tr key={p.id} data-testid={`payroll-history-row-${p.id}`}>
                      <td className="font-medium">{p.start_date} → {p.end_date}</td>
                      <td>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_STYLES[p.status]}`}>
                          {STATUS_LABEL[p.status]}
                        </span>
                      </td>
                      <td className="text-xs">
                        <div>{p.approved_by || "—"}</div>
                        <div className="text-slate-400">{p.approved_at ? new Date(p.approved_at).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                      </td>
                      <td className="text-xs">
                        <div>{p.exported_by || "—"}</div>
                        <div className="text-slate-400">{p.exported_at ? new Date(p.exported_at).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                      </td>
                      <td className="text-xs tabular-nums">{p.line_count ?? "—"}</td>
                      <td className="text-right tabular-nums font-medium">
                        {money(p.totals?.estimated_cost, p.approved_currency || currency)}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" className="h-7 text-xs mr-1"
                                onClick={() => redownload(p.id, p.start_date, "csv")}
                                data-testid={`btn-redownload-csv-${p.id}`}>
                          <Download className="w-3 h-3 mr-1" /> CSV
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => redownload(p.id, p.start_date, "xlsx")}
                                data-testid={`btn-redownload-xlsx-${p.id}`}>
                          <Download className="w-3 h-3 mr-1" /> XLSX
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Print stylesheet — makes the Payroll Summary drawer print as a
          clean single-page report (header + table only, no chrome). */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 10mm 8mm 12mm 8mm; }
          body * { visibility: hidden !important; }
          [data-testid="payroll-drawer"], [data-testid="payroll-drawer"] * {
            visibility: visible !important;
          }
          [data-testid="payroll-drawer"] {
            position: fixed !important; inset: 0 !important; max-width: 100% !important;
            max-height: 100% !important; width: 100% !important; height: 100% !important;
            border: 0 !important; box-shadow: none !important; padding: 0 !important;
            transform: none !important; background: #fff !important; overflow: visible !important;
          }
          [data-testid="payroll-drawer"] button,
          [data-testid="payroll-drawer"] input,
          [data-testid="payroll-drawer"] .print\\:hidden { display: none !important; }
          [data-testid="payroll-lines-table"] { font-size: 10px !important; width: 100% !important; }
          [data-testid="payroll-lines-table"] th,
          [data-testid="payroll-lines-table"] td {
            padding: 4px 6px !important; border: 1px solid #cbd5e1 !important;
          }
          [data-testid="payroll-lines-table"] thead th {
            background: #0f172a !important; color: #fff !important;
            -webkit-print-color-adjust: exact; print-color-adjust: exact;
          }
          [data-testid="payroll-lines-table"] tfoot tr {
            background: #f1f5f9 !important; font-weight: 700 !important;
            -webkit-print-color-adjust: exact; print-color-adjust: exact;
          }
          [data-testid="payroll-status-pill"] { border: 1px solid #94a3b8 !important; }
        }
      `}</style>
    </>
  );
}
